import React from "react";
import { Box, Typography, Grid, Card } from "@mui/material";
import clientImg from "../../assets/images/Automobile/phone-contact.gif";
import firstResponseImg from "../../assets/images/Automobile/customer-care.gif";
import assistanceArrivesImg from "../../assets/images/Automobile/bus-journey.gif";

/**
 * How It Works - Simple Flow for Automobile Assistance (Call Us > 2hr Response > AA Person Reaches)
 */

const FLOW = [
  {
    icon: (
      <img
        src={clientImg}
        alt="Call Across Assist"
        style={{ width: "100%", height: "100%" }}
      />
    ),
    label: "Call Across Assist",
    desc: "Assistance begins instantly through our 24×7 crisis expert agents.",
  },
  {
    icon: (
      <img
        src={firstResponseImg}
        alt="Help Arrives "
        style={{ width: "100%", height: "100%" }}
      />
    ),
    label: "Help Arrives ",
    desc: "Our support team dispatches experts to your incident location within 2 hours.",
  },
  {
    icon: (
      <img
        src={assistanceArrivesImg}
        alt="Continue Your Journey"
        style={{ width: "100%", height: "100%" }}
      />
    ),
    label: "Continue Your Journey",
    desc: "No matter the issue, we ensure you’re back on the road safely and without delay.",
  },
];

// Unify card width and height using fixed width and height
const UNIFIED_CARD_DIMENSIONS = {
  width: { xs: 240, sm: 260, md: 280 },
  height: { xs: 300, sm: 300, md: 280 },
};

const flowCardStyle = {
  card: {
    px: { xs: 2.5, sm: 2 },
    py: { xs: 3.5, sm: 4 },
    background: "#fff",
    borderRadius: "18px",
    border: "1px solid #dfc4b6ff",
    // boxShadow: "0 4px 32px 0 #e7d5bf46",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    ...UNIFIED_CARD_DIMENSIONS,
    minWidth: UNIFIED_CARD_DIMENSIONS.width, // Set minWidth, maxWidth, width
    maxWidth: UNIFIED_CARD_DIMENSIONS.width,
    minHeight: UNIFIED_CARD_DIMENSIONS.height,
    maxHeight: UNIFIED_CARD_DIMENSIONS.height,
    // border: "1px solid #fff6f1",
    transition:
      "transform 0.22s cubic-bezier(.36,1.03,.6,.98), box-shadow 0.22s",
    willChange: "transform,box-shadow",
    "&:hover": {
      transform: "rotateZ(-2.7deg) scale(1.04)",
      boxShadow: "0 8px 34px 4px #ffe0c0b6",
      borderColor: "#ffe0c0",
    },
    boxSizing: "border-box",
  },
  iconWrapper: {
    mb: 2,
    // background: "#f2f5ff",
    borderRadius: "50%",
    width: 73,
    height: 73,
    // boxShadow: "0 2px 10px #e8e2d9cc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  label: {
    fontWeight: 500,
    fontSize: { xs: "1.08rem", sm: "1.15rem" },
    mt: 0,
    color: "#251a0f",
    // fontFamily: "'Poppins',serif",
    fontFamily: "'Gilroy', sans-serif",
    mb: 0.5,
    letterSpacing: "0.0105em",
    textAlign: "center",
  },
  desc: {
    color: "#44423e",
    fontSize: { xs: "0.97rem", md: "1.01rem" },
    textAlign: "justify",
    fontFamily: "'Gilroy', sans-serif",
    mt: 0.3,
    px: 1,
    flexGrow: 1, // Allows description block to stretch vertically as needed for alignment
    display: "flex",
    alignItems: "center",
  },
};

const ArrowRight = () => (
  <Box
    sx={{ mx: { xs: 0.5, sm: 2 }, my: { xs: 2, sm: 0 } }}
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <svg
      width="38"
      height="38"
      viewBox="0 0 46 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 16.5H43M43 16.5L31.5 6M43 16.5L31.5 27"
        stroke="#FE7C0B"
        strokeWidth="2.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

const HowItWorksAutomobileFlow = () => (
  <Box
    sx={{
      background: "linear-gradient(160deg, #fff6f1 80%, #faeee4 100%)",
      px: { xs: 1, sm: 4, md: 4 },
      py: { xs: 3.5, sm: 4, md: 6.5 },
    }}
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
  >
    <Box mb={4} textAlign="center">
      <Typography
        variant="h4"
        className="main-heading-font-size"
        sx={{
          fontWeight: 500,
          color: "#251a0f",
          fontFamily: "'Poppins',serif",
        }}
      >
        How it Works
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{
          color: "#555b60",
          mt: 1,
          fontFamily: "'Poppins',serif",
          fontWeight: 400,
          fontSize: { xs: "1.04rem", sm: "1.12rem" },
        }}
      >
        Quick, caring, and effective support—here’s how your journey with Across
        Assist works.
      </Typography>
    </Box>
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      spacing={{ xs: 1, sm: 2, md: 2 }}
      sx={{
        maxWidth: 970,
        margin: "auto",
        flexWrap: { xs: "wrap", sm: "nowrap", md: "nowrap" },
      }}
      wrap="nowrap"
    >
      {FLOW.map((step, idx) => (
        <React.Fragment key={step.label}>
          <Grid item xs={12} sm auto>
            <Card elevation={0} sx={flowCardStyle.card}>
              <Box sx={flowCardStyle.iconWrapper} 
              className="gif-color-change"
              >{step.icon}</Box>
              <Typography sx={flowCardStyle.label}>
                <strong>{step.label}</strong>
              </Typography>
              <Typography sx={flowCardStyle.desc}>{step.desc}</Typography>
            </Card>
          </Grid>
          {idx < FLOW.length - 1 && (
            <Grid
              item
              sx={{
                display: { xs: "none", md: "flex", sm: "flex" },
                alignItems: "center",
                justifyContent: "center",
                minWidth: { sm: 45, md: 56 },
                maxWidth: { sm: 56, md: 85 },
              }}
            >
              <ArrowRight />
            </Grid>
          )}
        </React.Fragment>
      ))}
    </Grid>
  </Box>
);

export default HowItWorksAutomobileFlow;
